;(() => {
  const namespace = (globalThis.StylistLocator = globalThis.StylistLocator || {})

  // ============================================================================
  // Dependencies
  // ============================================================================

  const { templates = {}, utils = {}, geometry = {} } = namespace
  const { buildListBadge = () => '' } = templates
  const { escapeHtml = (v) => (v == null ? '' : String(v)), encodeKey = (v) => v } = utils
  const { determineRoleCategory = () => 'default', hasValidStylistRole = () => false } = geometry

  // ============================================================================
  // List Rendering
  // ============================================================================

  /**
   * Render the stylist list to desktop and mobile containers.
   * @param {HTMLElement} listCount - Element to display count
   * @param {HTMLElement} listDesktop - Desktop list container
   * @param {HTMLElement} listMobile - Mobile list container
   * @param {Array} stylists - Array of stylists to render
   * @param {string} selectedStylistEmail - Currently selected stylist email
   * @param {Object} callbacks - Event callbacks
   */
  function renderList(listCount, listDesktop, listMobile, stylists, selectedStylistEmail, callbacks) {
    const { onListItemClick = () => {} } = callbacks

    if (listCount) {
      const count = stylists.length
      listCount.textContent = formatStylistCount(count)
    }

    const markup = renderListMarkup(stylists, selectedStylistEmail)

    const attachClickHandlers = (container) => {
      container.querySelectorAll('[data-stylist-email]').forEach((el) => {
        el.addEventListener('click', () => onListItemClick(el.dataset.stylistEmail))
      })
    }

    if (listDesktop) {
      listDesktop.innerHTML = markup
      attachClickHandlers(listDesktop)
    }

    if (listMobile) {
      listMobile.innerHTML = markup
      attachClickHandlers(listMobile)
    }
  }

  /**
   * Generate HTML markup for the stylist list.
   * @param {Array} stylists - Array of stylists
   * @param {string} selectedStylistEmail - Currently selected stylist email
   * @returns {string} HTML markup
   */
  function renderListMarkup(stylists, selectedStylistEmail) {
    if (stylists.length === 0) {
      return '<div class="stylist-locator__empty">No stylists found in this area yet.</div>'
    }

    return stylists
      .map((stylist) => {
        const emailKey = encodeKey(stylist.email)
        const name = escapeHtml(stylist.name || 'Unnamed Stylist')
        // For salon owners without a valid stylist role, always show "Certified Salon"
        const isSalonOnly = stylist.isSalonOwner === true && !hasValidStylistRole(stylist)
        const role = escapeHtml(isSalonOnly ? 'Certified Salon' : (stylist.role || 'IBE® Stylist'))
        const isActive = stylist.email === selectedStylistEmail

        const roleCategory = stylist.roleCategory || determineRoleCategory(stylist)
        stylist.roleCategory = roleCategory

        const avatar = buildAvatarMarkup(stylist, name)

        return `
          <article class="stylist-locator__card stylist-locator__card--${roleCategory || 'default'}${isActive ? ' stylist-locator__card--active' : ''}" data-stylist-email="${emailKey}">
            <div class="stylist-locator__card-inner">
              <div class="stylist-locator__avatar">${avatar}</div>
              <div class="stylist-locator__card-meta">
                <div class="stylist-locator__card-name">${name}</div>
                <div class="stylist-locator__card-role">${role}</div>
              </div>
            </div>
            ${buildListBadge(stylist, roleCategory, isActive)}
          </article>
        `
      })
      .join('')
  }

  function formatStylistCount(count) {
    return `${count.toLocaleString()} ${count === 1 ? 'Stylist' : 'Stylists'}`
  }

  function getInitials(name) {
    return (name || '')
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0])
      .join('')
      .slice(0, 2)
  }

  function buildAvatarMarkup(stylist, safeName) {
    if (stylist.profile_photo) {
      const avatarSrc = escapeHtml(stylist.profile_photo)
      return `<img src="${avatarSrc}" alt="${safeName}">`
    }

    const initials = getInitials(stylist.name)
    return `<span>${initials || 'IB'}</span>`
  }

  // ============================================================================
  // Exports
  // ============================================================================

  namespace.list = Object.assign({}, namespace.list, {
    renderList,
    renderListMarkup,
  })
})()
